import { InlineAlertType, Query } from '@varo/graphql-schema-types';
import ADP from 'assets/images/directDeposit/ADP.png';
import Amazon from 'assets/images/directDeposit/Amazon.png';
import DoorDash from 'assets/images/directDeposit/DoorDash.png';
import Uber from 'assets/images/directDeposit/Uber.png';
import Walmart from 'assets/images/directDeposit/Walmart.png';
import { setStringAsync } from 'expo-clipboard';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  usePathname,
  useRouter,
} from 'expo-router';
import { downloadDirectDepositForm } from 'features/directDeposit/helpers/downloadForm';
import { useSetupDirectDeposit } from 'features/directDeposit/hooks/useSetupDirectDeposit';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, ScrollView, StyleSheet, View } from 'react-native';
import { segmentClient } from 'services/analytics/segment/segment';
import FetchDirectDepositAccountInfo from 'services/graphql/queries/MoveMoney/DirectDepositAccountInfo';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSBoldText from 'shared/components/vxl/typography/primary/body/BodyXSBoldText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { isWeb } from 'shared/constants/platform';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useAutoHideSnackbar } from 'shared/hooks/snackBar/useAutoHideSnackbar';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { useQuery } from 'urql';

import DirectDepositLoader from './DirectDepositLoader';

interface DirectDepositSetupIntroScreenProps {
  onInitiatePinwheel: () => void;
  isLoadingConditions?: boolean;
  isPinwheelLoading?: boolean;
  isCoinLoader?: boolean;
  titleText?: string;
  isLoading: boolean;
  sourceName?: string;
  abCohort?: string;
}

function DirectDepositSetupIntroScreen({
  onInitiatePinwheel,
  isLoadingConditions,
  isPinwheelLoading,
  isCoinLoader,
  titleText,
  isLoading,
  sourceName,
  abCohort,
}: DirectDepositSetupIntroScreenProps) {
  const router = useRouter();
  const { id, flowId, source } = useLocalSearchParams<{
    id?: string;
    flowId?: string;
    source?: string;
  }>();

  const { t: translatedText } = useTranslation('directDeposit');
  const { defaultMessage, graphqlErrorsTitle } = useDefaultErrorStrings();
  const [showSnackBar, setShowSnackBar] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState('');

  const [{ data: accountData }] = useQuery<Query>({
    query: FetchDirectDepositAccountInfo,
    requestPolicy: 'cache-and-network',
  });
  const pathname = usePathname();

  const directDepositAccount =
    accountData?.myAccounts?.forDirectDepositSetup?.[0];
  const accountNumber = directDepositAccount?.fullAccountNumber ?? '';
  const routingNumber = directDepositAccount?.routingNumber ?? '';

  const { pdfURL, error } = useSetupDirectDeposit(directDepositAccount?.id);

  const customAttributes = {
    screen_name: 'Direct Deposit Setup Intro Screen',
    screen_url: pathname,
    source_name: sourceName ?? '',
    ab_cohort: abCohort ?? '',
  };

  useViewDidFocusScreenAnalyticsEvent({
    key: 'dd_improvements_experiment',
    customAttributes,
  });

  const imageConfigs = [
    {
      id: 'adp',
      label: translatedText('adpIllustration'),
      source: <Image source={ADP} style={styles.logoImage} />,
    },
    {
      id: 'amazon',
      label: translatedText('amazonIllustration'),
      source: <Image source={Amazon} style={styles.logoImage} />,
    },
    {
      id: 'doordash',
      label: translatedText('doordashIllustration'),
      source: <Image source={DoorDash} style={styles.logoImage} />,
    },
    {
      id: 'uber',
      label: translatedText('uberIllustration'),
      source: <Image source={Uber} style={styles.logoImage} />,
    },
    {
      id: 'walmart',
      label: translatedText('walmartIllustration'),
      source: <Image source={Walmart} style={styles.logoImage} />,
    },
    {
      id: 'plus-more',
      label: translatedText('plusFortyThousandMore'),
      source: (
        <BodyXSBoldText style={styles.plusMoreText}>
          {translatedText('plusFortyThousand')}
        </BodyXSBoldText>
      ),
    },
  ];

  const handleDownloadForm = useCallback(async () => {
    void segmentClient.logEvent('Direct Deposit Setup Initiated', {
      category: 'direct_deposit',
      ...(sourceName ? { source_name: sourceName } : {}),
      ...(abCohort ? { ab_cohort: abCohort } : {}),
      method: 'manual',
    });
    if (!pdfURL) {
      return;
    }

    if (isWeb) {
      await downloadDirectDepositForm(pdfURL);
      return;
    }

    router.push({
      pathname: '/move-money/direct-deposit/direct-deposit-form',
      params: {
        id: directDepositAccount?.id ?? '',
      },
    });
  }, [pdfURL, directDepositAccount?.id, sourceName, abCohort, router]);

  useAutoHideSnackbar(Boolean(showSnackBar), () => {
    setShowSnackBar(false);
  });

  const copyToClipboard = useCallback(
    (text: string | null | undefined, message?: string) => {
      if (text) {
        void setStringAsync(text);
        setSnackBarMessage(message ?? translatedText('copied'));
      } else {
        setSnackBarMessage(translatedText('exceptionalError'));
      }

      setShowSnackBar(true);
    },
    [translatedText],
  );

  const handleInitiatePinwheel = () => {
    void segmentClient.logEvent('Direct Deposit Setup Initiated', {
      category: 'direct_deposit',
      ...(sourceName ? { source_name: sourceName } : {}),
      ...(abCohort ? { ab_cohort: abCohort } : {}),
      method: 'automated',
    });
    onInitiatePinwheel();
  };

  return (
    <>
      <Stack.Screen
        options={{
          title: translatedText('title'),
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              options={{
                title: '',
                headerBackVisible: true,
              }}
            />
          ),
        }}
      />

      <View style={styles.bodyContentContainer}>
        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollViewContentContainer}
        >
          <View style={styles.alertContainer}>
            <InlineAlert
              alertType={InlineAlertType.Brand}
              iconKeyType="PIGGY_BANK"
              onCardPress={() => {
                if (sourceName === 'onboarding_funding') {
                  const onboardingFlowId = id ?? flowId;
                  router.push({
                    pathname: '/onboarding/direct-deposit-benefits',
                    params: {
                      ...(onboardingFlowId ? { id: onboardingFlowId } : {}),
                      ...(source ? { source } : {}),
                      ...(abCohort ? { abCohort } : {}),
                      fromSetupIntro: 'true',
                    },
                  });
                } else {
                  router.push({
                    pathname: '/direct-deposit-benefits',
                    params: {
                      fromSetupIntro: 'true',
                      ...(abCohort ? { abCohort } : {}),
                    },
                  });
                }
              }}
              titleText={translatedText(
                'directDepositSetupIntroScreenAlertInline',
              )}
            />
          </View>
          <View style={styles.primaryContentInner}>
            <View style={styles.textGroup}>
              <TitleLgText style={styles.title} numberOfLines={0}>
                {translatedText('directDepositSetupIntroScreenTitle')}
              </TitleLgText>
              <BodyBaseText numberOfLines={0}>
                {translatedText('directDepositSetupIntroScreenDescription')}
              </BodyBaseText>
            </View>
            <View style={styles.imageContainer}>
              {imageConfigs.map((image) => (
                <View
                  style={[
                    image.id === 'plus-more'
                      ? styles.badgeCard
                      : styles.logoCard,
                  ]}
                  key={image.id}
                  accessibilityLabel={image.label}
                >
                  {image.source}
                </View>
              ))}
            </View>
            <View style={styles.buttonWrapper}>
              <Button
                label={translatedText('directDepositSetupIntroScreenButton')}
                onPress={handleInitiatePinwheel}
                analyticsId="findYourEmployer"
              />
            </View>
            <View style={styles.footerText}>
              <BodyXSText numberOfLines={0} style={styles.disclaimerTextColor}>
                {translatedText('directDepositSetupIntroScreenPinwheelInfo')}
              </BodyXSText>
            </View>
          </View>
          <View style={styles.dividerContainer} />
          <View
            style={[styles.primaryContentInner, styles.manualSectionContainer]}
          >
            <TitleMdText style={styles.manualSetupTitle} numberOfLines={0}>
              {translatedText('directDepositSetupIntroScreenManualTitle')}
            </TitleMdText>
            <BodyBaseText>
              {translatedText('directDepositSetupIntroScreenManualDescription')}
            </BodyBaseText>
          </View>
          <View style={styles.listGroup}>
            <ListItem
              primaryCopy={accountNumber}
              secondaryCopy={translatedText('accountNumberLabel')}
              lineFormat={ListItemLineFormat.LABEL}
              showsChevron={false}
              trailingAccessory={{
                id: '1',
                name: VXLComponentId.ICON,
                props: {
                  iconKey: 'COPY',
                  iconAccessibilityLabel: 'accountNumber-copy-icon',
                },
              }}
              onPress={() => {
                copyToClipboard(
                  accountNumber,
                  translatedText('accountNumberCopied'),
                );
              }}
            />
            <View style={styles.dividerWrapper}>
              <HairLineDivider />
            </View>

            <ListItem
              primaryCopy={routingNumber}
              secondaryCopy={translatedText('routingNumberLabel')}
              lineFormat={ListItemLineFormat.LABEL}
              showsChevron={false}
              trailingAccessory={{
                id: '1',
                name: VXLComponentId.ICON,
                props: {
                  iconKey: 'COPY',
                  iconAccessibilityLabel: 'routingNumber-copy-icon',
                },
              }}
              onPress={() => {
                copyToClipboard(
                  routingNumber,
                  translatedText('routingNumberCopied'),
                );
              }}
            />
            <View style={styles.dividerWrapper}>
              <HairLineDivider />
            </View>

            {pdfURL ? (
              <ListItem
                primaryCopy={translatedText(
                  'directDepositSetupIntroScreenDownloadButton',
                )}
                onPress={handleDownloadForm}
              />
            ) : null}
          </View>
        </ScrollView>
      </View>

      {showSnackBar ? (
        <Snackbar
          text={snackBarMessage}
          buttonOnPress={() => setShowSnackBar(false)}
        />
      ) : null}
      <ScreenTransitionOverlay
        isLoading={isLoading}
        LoadingComponent={
          <DirectDepositLoader
            isLoadingConditions={isLoadingConditions}
            isPinwheelLoading={isPinwheelLoading}
            defaultLoadingComponent={
              <FullScreenLoading
                isCoinLoader={isCoinLoader}
                titleText={titleText}
              />
            }
          />
        }
        ErrorComponent={
          error ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={graphqlErrorsTitle}
              messageBody={defaultMessage}
              primaryButton={{
                title: translatedText('errors.goToHomePageButtonLabel'),
                onPress: () => {
                  router.push('/');
                },
              }}
            />
          ) : null
        }
      />
    </>
  );
}

const styles = StyleSheet.create({
  bodyContentContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  scrollView: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  scrollViewContentContainer: {
    flexGrow: 1,
  },
  alertContainer: {
    marginTop: theme.spacing.m,
    marginHorizontal: theme.spacing.m,
    borderRadius: 4,
    overflow: 'hidden',
  },
  textGroup: {
    marginBottom: theme.spacing.l,
  },
  primaryContentInner: {
    paddingTop: theme.spacing.l,
    paddingHorizontal: theme.spacing.m,
    width: '100%',
  },
  imageContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginTop: 0,
    marginBottom: theme.spacing.l,
    alignSelf: 'stretch',
    gap: 3,
  },
  buttonWrapper: {
    marginTop: 0,
    marginBottom: 20,
  },
  logoCard: {
    width: theme.spacing.xxl,
    height: theme.spacing.xxl,
    borderWidth: 0.5,
    borderColor: theme.color.border.primary,
    borderRadius: 8,
    backgroundColor: theme.color.background.primary,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  logoImage: {
    width: '75%',
    height: '75%',
    resizeMode: 'contain',
  },
  badgeCard: {
    height: theme.spacing.xxl,
    paddingHorizontal: 10,
    backgroundColor: theme.color.background.surface,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
  },
  plusMoreText: {
    fontWeight: 500,
  },
  title: {
    lineHeight: 32,
    marginBottom: 12,
    fontWeight: 500,
  },
  manualSetupTitle: {
    fontSize: 18,
    marginBottom: 12,
  },
  footerText: {
    paddingVertical: 0,
  },
  disclaimerTextColor: {
    color: theme.color.text.secondary,
    fontSize: 13,
  },
  dividerContainer: {
    height: 8,
    backgroundColor: theme.color.background.surface,
    marginTop: theme.spacing.l,
    marginBottom: theme.spacing.l,
  },
  manualSectionContainer: {
    paddingTop: 0,
  },
  listGroup: {
    alignSelf: 'stretch',
    marginTop: theme.spacing.xs,
    paddingBottom: 40,
  },
  dividerWrapper: {
    paddingHorizontal: 16,
  },
});

export default DirectDepositSetupIntroScreen;
